import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import { onlyNums } from "../../utils/OnlyNums";
import { sanitizeNumber } from "../../utils/sanitizeNumber";

// Import discount services
import {
  updateDiscount,
  deleteDiscount,
} from "../../services/schoolDiscountsServices";

// Import icons
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import editIcon from "../../assets/edit_icon.png";
import AreYouSureModal from "../../components/AreYouSureModal";

const DiscountInfoModal = ({ discount, onClose, getAllDiscounts }) => {
  const { t } = useTranslation("general");

  // State variables
  const [isEditing, setIsEditing] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [originalValues, setOriginalValues] = useState({
    amount: 0,
  });

  // Form fields
  const [discountFields, setDiscountFields] = useState({
    name: "",
    isPercentage: false,
    amount: 0,
  });

  // Initialize form fields when component mounts or discount changes
  useEffect(() => {
    if (discount) {
      setOriginalValues({
        amount: discount.amount || 0,
      });

      setDiscountFields({
        name: discount.name || "",
        isPercentage: discount.isPercentage,
        amount: discount.amount || 0,
      });
    }
  }, [discount]);

  // Handle input changes
  const handleInputChange = (e, fieldName) => {
    const value = e.target?.value ?? "";
    setDiscountFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear error when user starts typing
    if (error) {
      setError(false);
      setErrorMessage("");
    }
  };

  // Handle discount type change
  const handleDiscountTypeChange = (isPercentage) => {
    setDiscountFields((prev) => ({
      ...prev,
      isPercentage,
      amount: originalValues.amount || 0,
    }));
  };

  // Handle discount value change
  const handleDiscountValueChange = (e) => {
    const value = e.target?.value ?? 0;

    if (discountFields.isPercentage && parseFloat(value) > 100) {
      return;
    }

    setDiscountFields((prev) => ({
      ...prev,
      amount: value,
    }));
  };

  // Handle save discount - FIXED VERSION
  // Handle save discount - FIXED VERSION
  const handleSaveDiscount = async () => {
    // Validation
    if (!discountFields.name.trim()) {
      setError(true);
      setErrorMessage(t("noDiscountName"));
      return;
    }

    if (!discountFields.amount || parseFloat(discountFields.amount) <= 0) {
      setError(true);
      setErrorMessage(t("invalidDiscountValue"));
      return;
    }

    try {
      setLoadingModal(true);
      setError(false);

      const updateData = {
        name: discountFields.name.trim(),
        isPercentage: discountFields.isPercentage,
        amount: parseFloat(discountFields.amount),
      };

      const response = await updateDiscount(discount.id, updateData);

      if (response?.data) {
        // ✅ UPDATE: Update local state with fresh data from server
        const updatedDiscount = response.data.discount;
        console.log("Updated Discount:", response.data.discount);
        setDiscountFields({
          name: updatedDiscount.name,
          isPercentage: updatedDiscount.isPercentage,
          amount: updatedDiscount.amount,
        });

        // ✅ UPDATE: Update original values for future edits
        setOriginalValues({
          amount: updatedDiscount.amount || discountFields.amount,
        });

        setIsEditing(false);
        getAllDiscounts(); // Refresh the discounts list
      }
    } catch (err) {
      console.error("Error updating discount:", err);
      setError(true);
      setErrorMessage(
        err.response?.data?.message || t("errorUpdatingDiscount")
      );
    } finally {
      setLoadingModal(false);
    }
  };

  // Handle delete discount
  const handleDeleteDiscount = async () => {
    try {
      setLoadingModal(true);
      setError(false);

      const response = await deleteDiscount(discount.id);

      if (response?.status === 200) {
        getAllDiscounts(); // Refresh the discounts list
        onClose();
      }
    } catch (err) {
      console.error("Error deleting discount:", err);
      setError(true);
      setErrorMessage(
        err.response?.data?.message || t("errorDeletingDiscount")
      );
    } finally {
      setLoadingModal(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (isEditing) {
      // Reset form fields to original values
      setDiscountFields({
        name: discount.name || "",
        isPercentage: discount.isPercentage || true,
        amount: discount.amount || 0,
      });
      setIsEditing(false);
      setError(false);
      setErrorMessage("");
    } else {
      onClose();
    }
  };

  if (!discount) return null;

  return (
    <>
      <Modal
        title={isEditing ? t("editDiscount") : t("discountInfo")}
        onClose={handleCancel}
        footer={
          <>
            {loadingModal ? <Loader /> : null}
            <section className="edit-user-actions">
              {isEditing ? (
                <>
                  <CustomButton
                    onClick={handleSaveDiscount}
                    title={t("confirm")}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancel}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                  <CustomButton
                    variant="red"
                    title={t("delete")}
                    onClick={() => setShowDeleteConfirmation(true)}
                    icon={deleteIcon}
                    iconSize={16}
                  />
                </>
              ) : (
                <>
                  <CustomButton
                    variant="blank"
                    title={t("edit")}
                    onClick={() => setIsEditing(true)}
                    icon={editIcon}
                    iconSize={18}
                  />
                </>
              )}
            </section>
          </>
        }
      >
        {error && <ErrorBox>{errorMessage}</ErrorBox>}
        <div className="o-column o-column--gap-size_400">
          {/* Discount Name */}
          <section>
            {isEditing ? (
              <div className="o-row o-row--gap-size_700 o-row--vertical-align_center u-min-width-specific_400">
                <span className="u-min-width-specific_80">{t("name")}:</span>
                <CustomInput
                  name="name"
                  value={discountFields.name}
                  onChange={(e) => handleInputChange(e, "name")}
                  errorText={
                    error && !discountFields.name.trim()
                      ? t("noDiscountName")
                      : ""
                  }
                  flex={true}
                />
              </div>
            ) : (
              <div className="o-row o-row--gap-size_700 o-row--vertical-align_center u-min-width-specific_400">
                <span className="u-min-width-specific_80">{t("name")}:</span>
                <span>{discountFields.name}</span>
              </div>
            )}
          </section>

          {/* Discount Type */}
          <section>
            {isEditing ? (
              <div className="o-column u-margin--bottom_100 o-column--gap-size_300">
                <div className="o-row o-row--vertical-align_center o-row--gap-size_400">
                  <input
                    type="radio"
                    name="discount"
                    checked={discountFields.isPercentage}
                    onChange={() => handleDiscountTypeChange(true)}
                  />
                  <label htmlFor="percentageDiscount">
                    {t("discountByPercentage")}
                  </label>
                </div>
                <div className="o-row o-row--vertical-align_center o-row--gap-size_400">
                  <input
                    type="radio"
                    name="discount"
                    checked={!discountFields.isPercentage}
                    onChange={() => handleDiscountTypeChange(false)}
                  />
                  <label htmlFor="amountDiscount">
                    {t("discountByAmount")}
                  </label>
                </div>
              </div>
            ) : (
              <div className="o-row o-row--gap-size_700 o-row--vertical-align_center u-min-width-specific_400">
                <span className="u-min-width-specific_80">
                  {t("discountType")}:
                </span>
                <span>
                  {discountFields.isPercentage
                    ? t("byPercentage")
                    : t("byAmount")}
                </span>
              </div>
            )}
          </section>

          {/* Discount Value */}
          <section>
            {isEditing ? (
              <div className="o-row o-row--gap-size_700 o-row--vertical-align_center u-min-width-specific_400">
                <span className="u-min-width-specific_80">{t("value")}:</span>
                <CustomInputWithUnit
                  name="amount"
                  value={discountFields.amount}
                  onChange={handleDiscountValueChange}
                  onKeyDown={onlyNums}
                  flex={true}
                  unit={discountFields.isPercentage ? "%" : t("IQD")}
                  min={0}
                  max={discountFields.isPercentage ? 100 : undefined}
                  type="moneyV2"
                  errorText={
                    error && !discountFields.amount
                      ? t("invalidDiscountValue")
                      : ""
                  }
                />
              </div>
            ) : (
              <div className="o-row o-row--gap-size_700 o-row--vertical-align_center u-min-width-specific_400">
                <span className="u-min-width-specific_80">{t("value")}:</span>
                <span>
                  {discountFields.isPercentage
                    ? `${discount.amount} %`
                    : `${discount.amount} ${t("IQD")}`}
                </span>
              </div>
            )}
          </section>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <AreYouSureModal
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDeleteDiscount}
          customMessage={t("confirmDeleteDiscount")}
          closeTitle="cancel"
          confirmTitle="confirm"
          loading={loadingModal}
          cancel
          confirmIconType="whiteCheck"

        />
      )}
    </>
  );
};

export default DiscountInfoModal;
